import { isAfter, isValid, parse } from 'date-fns';
import i18next from 'i18next';
import * as z from 'zod';

import { attachmentsValidation } from '@/components/ui/video-upload-form-field';
import { FREQUENCY_RANGES, FREQUENCY_UNITS, type FrequencyUnit } from '@/interfaces/IFrequencies';
import {
  isMissionNonKamikaze,
  isMissionNonLogistical,
  isMissionNonReconnaissance,
  isMissionNonRescue,
  MissionType
} from '@/interfaces/IMissionType';
import { IQuestionary } from '@/interfaces/IQuestionary';
import { IResultCategory } from '@/interfaces/IResultCategory';
import { isUVDamagedOrLost, isUVLost } from '@/interfaces/IUVCondition';

import { MGRS_PATTERN, OTHER_UV_ID } from './helpers';

export const validationSchema = (questionary: IQuestionary) => {
  return z
    .object({
      team_position: z.string().nonempty(i18next.t('validations.required')),
      team_name: z.string().optional(),
      commander_call_sign: z.string().optional(),
      operator_call_sign: z.string().nonempty(i18next.t('validations.required')),

      mission_type: z.string().nonempty(i18next.t('validations.required')),
      mission_start_date: z.string().nonempty(i18next.t('validations.required')),
      mission_start_time: z.string().nonempty(i18next.t('validations.required')),
      mission_end_date: z.string().optional(),
      mission_end_time: z.string().optional(),
      mission_number: questionary.mission_number
        ? z
            .preprocess(
              val => Number(val),
              z
                .number({ invalid_type_error: i18next.t('validations.integer') })
                .int()
                .min(1, i18next.t('validations.required'))
            )
            .optional()
        : z.string().optional(),

      target_code: z.string().optional(),
      target_coordinates: z.string().optional(),
      target_type_id: z.string().optional(),
      payload_weight: z.preprocess(val => Number(val), z.number().min(0, i18next.t('validations.number'))).optional(),
      travel_distance: z.preprocess(val => Number(val), z.number().min(0, i18next.t('validations.number'))).optional(),

      uv_type: z.string().nonempty(i18next.t('validations.required')),
      uv: z.string().optional(),
      other_uv: z.string().optional(),
      control_link_frequency: z
        .preprocess(v => (v === '' || v === null ? undefined : Number(v)), z.number().nonnegative().finite().optional())
        .optional(),
      control_link_frequency_unit: z.string().optional(),
      video_link_frequency: z
        .preprocess(v => (v === '' || v === null ? undefined : Number(v)), z.number().nonnegative().finite().optional())
        .optional(),
      video_link_frequency_unit: z.string().optional(),
      battery_id: z.string().optional(),
      uv_condition: z.string().optional(),
      uv_lost_reason: z.string().optional(),
      uv_last_known_coordinates: z.string().optional(),

      payloads: z
        .array(
          z.object({
            payload_type: z.string().optional(),
            payload_count: z.preprocess(
              val => (val ? Number(val) : val),
              z.number().int().min(1, i18next.t('validations.integer')).optional()
            )
          })
        )
        .max(4)
        .optional(),

      mission_status: z.string().optional(),
      mission_failure_reason: z.string().nonempty(i18next.t('validations.required')),
      attachments: attachmentsValidation(),
      stream: z.string().optional(),
      team_notes: z.string().optional()
    })
    .superRefine((data, ctx) => {
      if (questionary.team_name && !data.team_name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['team_name'],
          message: i18next.t('validations.required')
        });
      }
      if (questionary.commander_call_sign && !data.commander_call_sign) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['commander_call_sign'],
          message: i18next.t('validations.required')
        });
      }
    })
    .superRefine((data, ctx) => {
      const now = new Date();

      // Validate mission_started_at is not in future (date + time)
      if (data.mission_start_date && data.mission_start_time) {
        const startDateTime = parse(
          `${data.mission_start_date} ${data.mission_start_time}`,
          'dd.MM.yyyy HH:mm',
          new Date()
        );

        if (isValid(startDateTime) && isAfter(startDateTime, now)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mission_start_time'],
            message: i18next.t('validations.future_date')
          });
        }
      }

      // Validate mission_ended_at is not in future (date + time)
      if (data.mission_end_date && data.mission_end_time) {
        const endDateTime = parse(`${data.mission_end_date} ${data.mission_end_time}`, 'dd.MM.yyyy HH:mm', new Date());

        if (isValid(endDateTime) && isAfter(endDateTime, now)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mission_end_time'],
            message: i18next.t('validations.future_date')
          });
        }
      }

      // Validate mission_ended_at is after mission_started_at
      if (data.mission_start_date && data.mission_start_time && data.mission_end_date && data.mission_end_time) {
        const startDateTime = parse(
          `${data.mission_start_date} ${data.mission_start_time}`,
          'dd.MM.yyyy HH:mm',
          new Date()
        );
        const endDateTime = parse(`${data.mission_end_date} ${data.mission_end_time}`, 'dd.MM.yyyy HH:mm', new Date());

        if (isValid(startDateTime) && isValid(endDateTime) && !isAfter(endDateTime, startDateTime)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mission_end_time'],
            message: i18next.t('validations.end_time_before_start')
          });
        }
      }
    })
    .superRefine((data, ctx) => {
      if (
        isMissionNonLogistical(data.mission_type) &&
        isMissionNonRescue(data.mission_type) &&
        isMissionNonReconnaissance(data.mission_type)
      ) {
        if (questionary.target_code && !data.target_code) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['target_code'],
            message: i18next.t('validations.required')
          });
        }
        if (questionary.target_coordinates && !data.target_coordinates) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['target_coordinates'],
            message: i18next.t('validations.required')
          });
        }
        if (questionary.target_coordinates && data.target_coordinates && !MGRS_PATTERN.test(data.target_coordinates)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['target_coordinates'],
            message: i18next.t('validations.mgrs_format')
          });
        }
        if (!data.target_type_id) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['target_type_id'],
            message: i18next.t('validations.required')
          });
        }
      }

      if (questionary.mission_ended_at) {
        if (!data.mission_end_date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mission_end_date'],
            message: i18next.t('validations.required')
          });
        }
        if (!data.mission_end_time) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mission_end_time'],
            message: i18next.t('validations.required')
          });
        }
      }
    })
    .superRefine((data, ctx) => {
      if (!questionary.uv) {
        return;
      }

      if (!data.uv) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['uv'],
          message: i18next.t('validations.required')
        });

        return;
      } else if (data.uv === OTHER_UV_ID) {
        if (!data.other_uv) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['other_uv'],
            message: i18next.t('validations.required')
          });

          return;
        }
      }

      const allowedUVIds =
        questionary.uv
          ?.filter(option => option.uv_type === data.uv_type || !option.uv_type)
          ?.map(option => option.value) || [];

      if (!allowedUVIds.includes(data.uv)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['uv'],
          message: i18next.t('validations.correct_uv')
        });
      }
    })
    .superRefine((data, ctx) => {
      if (isMissionNonKamikaze(data.mission_type) && !data.uv_condition) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['uv_condition'],
          message: i18next.t('validations.required')
        });
      }
    })
    .superRefine((data, ctx) => {
      if (isUVLost(data.uv_condition) && questionary.uv_lost_reason && !data.uv_lost_reason) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['uv_lost_reason'],
          message: i18next.t('validations.required')
        });
      }
    })
    .superRefine((data, ctx) => {
      if (
        isMissionNonLogistical(data.mission_type) &&
        isMissionNonRescue(data.mission_type) &&
        isMissionNonReconnaissance(data.mission_type)
      ) {
        if (!data.payloads) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['payloads'],
            message: i18next.t('validations.required')
          });
          return;
        }

        data.payloads.forEach((payload, index) => {
          if (!payload.payload_type) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [`payloads.${index}.payload_type`],
              message: i18next.t('validations.required')
            });
          }

          if (questionary.payload_type_v2?.find(item => item.value === payload.payload_type)?.require_count) {
            if (!payload.payload_count) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [`payloads.${index}.payload_count`],
                message: i18next.t('validations.required')
              });
            }
          }
        });
      }
    })
    .superRefine((data, ctx) => {
      if (isMissionNonReconnaissance(data.mission_type)) {
        if (!data.mission_status) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mission_status'],
            message: i18next.t('validations.required')
          });
        }
      }
    })
    .superRefine((data, ctx) => {
      const allowedMissionStatuses =
        questionary.mission_status_v2
          ?.filter(option => option.mission_types?.includes(data.mission_type as MissionType) || !option.mission_types)
          ?.map(option => option.value) || [];

      if (data.mission_status && !allowedMissionStatuses.includes(data.mission_status)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['mission_status'],
          message: i18next.t('validations.correct_mission_status')
        });
      }
    })
    .superRefine((data, ctx) => {
      const toNumber = Number(data.mission_number);
      if (questionary.mission_number) {
        if (Number.isNaN(toNumber) || !Number.isFinite(toNumber)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mission_number'],
            message: i18next.t('validations.integer')
          });
        } else if (toNumber < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mission_number'],
            message: i18next.t('validations.required')
          });
        }
      } else {
        data.mission_number = 0;
      }
    })
    .superRefine((data, ctx) => {
      if (questionary.control_link_frequency && !!data.control_link_frequency) {
        const frequency = Number(data.control_link_frequency);
        const unit = data.control_link_frequency_unit as FrequencyUnit;

        if (unit === FREQUENCY_UNITS.GHZ) {
          const range = FREQUENCY_RANGES[FREQUENCY_UNITS.GHZ];
          if (frequency < range.min || frequency > range.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['control_link_frequency'],
              message: i18next.t('validations.frequency_ghz_range')
            });
          }
        } else {
          const range = FREQUENCY_RANGES[FREQUENCY_UNITS.MHZ];
          if (frequency < range.min || frequency > range.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['control_link_frequency'],
              message: i18next.t('validations.frequency_mhz_range')
            });
          }
        }
      }

      if (questionary.video_link_frequency && !!data.video_link_frequency) {
        const frequency = Number(data.video_link_frequency);
        const unit = data.video_link_frequency_unit as FrequencyUnit;

        if (unit === FREQUENCY_UNITS.GHZ) {
          const range = FREQUENCY_RANGES[FREQUENCY_UNITS.GHZ];
          if (frequency < range.min || frequency > range.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['video_link_frequency'],
              message: i18next.t('validations.frequency_ghz_range')
            });
          }
        } else {
          const range = FREQUENCY_RANGES[FREQUENCY_UNITS.MHZ];
          if (frequency < range.min || frequency > range.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['video_link_frequency'],
              message: i18next.t('validations.frequency_mhz_range')
            });
          }
        }
      }
    })
    .superRefine((data, ctx) => {
      if (!data.mission_failure_reason) {
        return;
      }

      const missionFailureReason = questionary.mission_failure_reason_v2?.find(
        reason => reason.value === data.mission_failure_reason
      );
      if (missionFailureReason && missionFailureReason.category !== IResultCategory.SUCCESS) {
        return;
      }

      const uvDamagedOrLost = isUVDamagedOrLost(data.uv_condition);
      if (uvDamagedOrLost) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['mission_failure_reason'],
          message: i18next.t('validations.failure_reason_invalid_for_damaged_uv')
        });
        return;
      }

      const resultUnsuccessful =
        !!data.mission_status &&
        questionary.mission_status_v2?.find(status => status.value === data.mission_status)?.category ===
          IResultCategory.FAILURE;

      if (resultUnsuccessful) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['mission_failure_reason'],
          message: i18next.t('validations.failure_reason_invalid_for_failed_mission')
        });
      }
    });
};
